import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AuthenticationEnrollment,
  AuthenticationMethod,
  deleteAuthenticationMethod,
  getMyAccountAudience,
  listAuthenticationMethods,
  MY_ACCOUNT_SCOPES,
  startAuthenticationMethod,
  verifyAuthenticationMethod,
} from '../../../services/myAccountMfa';
import './SecuritySettings.css';

function methodLabel(type: string): string {
  if (type === 'totp') return 'Authenticator app';
  if (type === 'recovery-code') return 'Recovery code';
  if (type === 'webauthn-roaming') return 'Security key';
  if (type === 'webauthn-platform') return 'Device authentication';
  if (type === 'push-notification') return 'Push notification';
  if (type === 'sms' || type === 'phone') return 'Text message';
  if (type === 'email') return 'Email';
  return 'Authentication method';
}

const SECONDARY_METHOD_TYPES = new Set([
  'totp',
  'recovery-code',
  'webauthn-roaming',
  'webauthn-platform',
  'push-notification',
  'sms',
  'phone',
  'email',
]);

export default function SecuritySettings(): React.ReactElement {
  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();
  const [methods, setMethods] = useState<AuthenticationMethod[]>([]);
  const [enrollment, setEnrollment] = useState<AuthenticationEnrollment | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'signin' | 'working' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const audience = useMemo(() => getMyAccountAudience(auth0Domain), [auth0Domain]);

  const getSecurityToken = useCallback(() => getAccessTokenSilently({
    authorizationParams: { audience, scope: MY_ACCOUNT_SCOPES },
  }), [audience, getAccessTokenSilently]);

  const loadMethods = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus('signin');
      return;
    }
    setStatus('loading');
    try {
      const token = await getSecurityToken();
      const nextMethods = await listAuthenticationMethods(auth0Domain, token);
      setMethods(nextMethods.filter((method) => method.confirmed !== false && SECONDARY_METHOD_TYPES.has(method.type)));
      setStatus('ready');
      setMessage('');
    } catch (error: any) {
      const interactive = ['login_required', 'consent_required', 'mfa_required', 'unmet_authentication_requirements'].includes(error?.error || error?.code);
      setStatus(interactive ? 'signin' : 'error');
      setMessage(interactive ? '' : 'Security settings are temporarily unavailable.');
    }
  }, [auth0Domain, getSecurityToken, isAuthenticated]);

  useEffect(() => {
    void loadMethods();
    return () => {
      setEnrollment(null);
      setOtpCode('');
    };
  }, [loadMethods]);

  async function secureSignIn() {
    await loginWithRedirect({
      appState: { returnTo: '/account-settings/security' },
      authorizationParams: {
        audience,
        scope: MY_ACCOUNT_SCOPES,
        prompt: 'login',
      },
    });
  }

  async function start(type: 'totp' | 'recovery-code') {
    setStatus('working');
    setMessage('');
    try {
      const token = await getSecurityToken();
      setEnrollment(await startAuthenticationMethod(auth0Domain, token, type));
      setStatus('ready');
    } catch (error: any) {
      if (['login_required', 'mfa_required', 'unmet_authentication_requirements'].includes(error?.error || error?.code)) {
        setStatus('signin');
      } else {
        setStatus('error');
        setMessage('Unable to start authentication setup.');
      }
    }
  }

  async function verify() {
    if (!enrollment) return;
    if (enrollment.type === 'totp' && !/^\d{6}$/.test(otpCode)) {
      setMessage('Enter the 6-digit code from Authy.');
      return;
    }
    setStatus('working');
    try {
      const token = await getSecurityToken();
      await verifyAuthenticationMethod(auth0Domain, token, enrollment, enrollment.type === 'totp' ? otpCode : undefined);
      setEnrollment(null);
      setOtpCode('');
      await loadMethods();
      setMessage('Security method added.');
    } catch {
      setStatus('ready');
      setMessage('That code did not work. Try the newest code from Authy.');
    }
  }

  async function remove(method: AuthenticationMethod) {
    const confirmed = methods.filter((item) => item.confirmed !== false);
    const hasReplacement = confirmed.some((item) => item.id !== method.id && item.type !== 'recovery-code');
    if (!hasReplacement) {
      setMessage('Add a replacement before removing your only security method.');
      return;
    }
    setStatus('working');
    try {
      const token = await getSecurityToken();
      await deleteAuthenticationMethod(auth0Domain, token, method.id);
      await loadMethods();
      setMessage('Security method removed.');
    } catch {
      setStatus('error');
      setMessage('Unable to remove that security method.');
    }
  }

  return (
    <div className="security-settings" data-testid="security-settings">
      <div className="settings-subpage-header security-settings__header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Security</h2>
          <p className="settings-subpage-description">Manage two-factor authentication.</p>
        </div>
      </div>

      {status === 'loading' && <p role="status">Loading security settings...</p>}
      {status === 'signin' && (
        <button className="btn btn-primary" type="button" onClick={secureSignIn}>Continue securely</button>
      )}
      {(status === 'ready' || status === 'working') && !enrollment && (
        <>
          <section className="security-settings__methods" aria-label="Two-factor authentication methods">
            {methods.length === 0 ? (
              <p>No two-factor authentication method is set up.</p>
            ) : methods.map((method) => (
              <div className="security-settings__method" key={method.id}>
                <div>
                  <strong>{methodLabel(method.type)}</strong>
                  <span>Enabled</span>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  disabled={status === 'working' || !methods.some((item) => item.id !== method.id && item.type !== 'recovery-code')}
                  onClick={() => remove(method)}
                >
                  Remove
                </button>
              </div>
            ))}
          </section>
          <div className="security-settings__actions">
            <button className="btn btn-primary" type="button" disabled={status === 'working'} onClick={() => start('totp')}>
              Add authenticator app
            </button>
            <button className="btn btn-outline-light" type="button" disabled={status === 'working'} onClick={() => start('recovery-code')}>
              Create recovery code
            </button>
          </div>
        </>
      )}

      {enrollment?.type === 'totp' && (
        <section className="security-settings__enrollment" aria-labelledby="authenticator-setup-title">
          <h3 id="authenticator-setup-title">Set up Authy</h3>
          {enrollment.barcode_uri && <a className="btn btn-primary" href={enrollment.barcode_uri}>Open in authenticator app</a>}
          {enrollment.manual_input_code && (
            <div className="security-settings__secret">
              <span>Manual setup code</span>
              <code>{enrollment.manual_input_code}</code>
            </div>
          )}
          <label htmlFor="authenticator-code">6-digit code</label>
          <input
            id="authenticator-code"
            className="form-control"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otpCode}
            onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <button className="btn btn-success" type="button" onClick={verify} disabled={status === 'working'}>Confirm</button>
        </section>
      )}

      {enrollment?.type === 'recovery-code' && (
        <section className="security-settings__enrollment" aria-labelledby="recovery-code-title">
          <h3 id="recovery-code-title">Save recovery code</h3>
          <code className="security-settings__recovery-code">{enrollment.recovery_code}</code>
          <button className="btn btn-success" type="button" onClick={verify} disabled={status === 'working'}>I saved it</button>
        </section>
      )}

      {message && <p className={status === 'error' ? 'security-settings__error' : 'security-settings__message'} role="status">{message}</p>}
    </div>
  );
}
